<template>
  <!-- THE PRODUCT'S MARK — « La route » (logo collection 01): the V is a route,
       two stops and a turn, and the peach disc is the next stop. The drawing
       lives in assets/brand/mark.svg, so the header, the login screen, the admin
       shell and every export (favicon, app icons, a letterhead) cut the same file.
       ⚠ It is INLINED, never an <img>: an <img> cannot read the page's CSS. The
       file reads --mark-stroke and --mark-dot from the theme (style.css): the
       route's teal, lighter on the dark shell; the disc's peach, the SAME in
       both themes — the disc is an object, not a surface. -->
  <span
    :class="stacked ? 'inline-flex flex-col items-center text-center' : 'inline-flex items-center gap-2.5'"
    :title="TITLE"
  >
    <!-- The disc's ring is the surface BEHIND it (the header's card, or the
         page ground on the signed-out screens), which is what keeps the disc
         legible where it sits on the end of the stroke — a solid, never a tint.
         v-html is safe here: the markup is a bundled asset, never user input. -->
    <!-- eslint-disable vue/no-v-html -->
    <span
      role="img" :aria-label="TITLE"
      :class="[
        'shrink-0 [&>svg]:block [&>svg]:size-full',
        ground === 'page' ? '[--mark-ring:var(--background)]' : '[--mark-ring:var(--card)]',
      ]"
      :style="{ width: `${size}px`, height: `${size}px` }"
      v-html="markSvg"
    />
    <!-- eslint-enable vue/no-v-html -->
    <!-- Stacked: the signed-out door's lockup. The name scales with the mark,
         and the line under it is a slot so the admin door can put its badge
         where « Terrain » sits. -->
    <span v-if="stacked" class="flex flex-col items-center leading-none">
      <span
        class="mt-3.5 font-extrabold tracking-tight text-foreground"
        :style="{ fontSize: `${Math.round(size * 0.53)}px` }"
      >Vitesse</span>
      <span class="mt-2">
        <slot>
          <span class="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Terrain</span>
        </slot>
      </span>
    </span>
    <!-- The name, plain (his ruling 2026-09-13): « Terrain » under « Vitesse »
         in small caps, which gives the product the same shape as the customer's
         identity on the other side of the header — a label over a name. -->
    <span v-else-if="!markOnly" class="flex flex-col leading-none">
      <span class="text-[18px] font-extrabold tracking-tight text-foreground">Vitesse</span>
      <span class="mt-[4px] text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Terrain</span>
    </span>
  </span>
</template>

<script setup lang="ts">
import markSvg from '@/assets/brand/mark.svg?raw'

// ⚰ « Vitesse .app » is gone: `.app` is an ADDRESS, not a name — nobody writes
// « Gmail.com » in their own header — and a logotype built out of CSS spans can
// never become a favicon, an app icon or a letterhead.
// 34 in a 64px header (his QA: the mark and the name were both too small to
// carry the column they sit on).
withDefaults(defineProps<{
  size?: number
  markOnly?: boolean
  stacked?: boolean
  // What the mark sits on — the peach dot's ring takes that surface's colour.
  ground?: 'card' | 'page'
}>(), {
  size: 34,
  markOnly: false,
  stacked: false,
  ground: 'card',
})

// Not translated: a product name is the same in every locale.
const TITLE = 'Vitesse Terrain'
</script>
